import { Injectable, inject } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import {
  deleteAllEntities,
  deleteEntities,
  selectAllEntities,
  upsertEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { throwError, Observable, forkJoin } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { v4 } from 'uuid';

import { ActiveCustomerService } from '@digital-platform/customers/services';
import { Generator } from '@digital-platform/generators/domain';
import { getCustomerAccessList } from '@digital-platform/shared/domain';
import { ErrorService } from '@digital-platform/shared/services';
import { LoginSessionFacadeService } from '@digital-platform/users/services';
import { GeneratorsService } from '../generators/generators.service';

const store = createStore(
  { name: 'generators' },
  withEntities<Generator>(),
  withProps<{
    activeGenerator: Generator | null;
    loading: boolean;
    loaded: boolean;
  }>({ activeGenerator: null, loading: false, loaded: false })
);

@Injectable({
  providedIn: 'root',
})
export class GeneratorsFacadeService {
  public generators$ = store.pipe(selectAllEntities());
  public activeGenerator$ = store.pipe(
    select((state) => state.activeGenerator)
  );
  public loading$ = store.pipe(select((state) => state.loading));
  public loaded$ = store.pipe(select((state) => state.loaded));

  private findRequests: { [key: string]: Observable<Generator> } = {};

  private selectedCustomer: string;
  private accessList: string[];
  private loadingId: string;

  // injected services
  private readonly errorService = inject(ErrorService);
  private readonly loginFacade = inject(LoginSessionFacadeService);
  private readonly service = inject(GeneratorsService);
  private readonly activeCustomerService = inject(ActiveCustomerService);

  constructor() {
    this.activeCustomerService.selectedCustomer$.subscribe((customer) => {
      this.selectedCustomer = customer;
    });

    this.loginFacade.loginSession$.subscribe((loginSession) => {
      this.accessList = getCustomerAccessList(loginSession?.user?.access);
      // Clear generators on logout
      if (!this.accessList || this.accessList.length === 0) {
        this.loadingId = '';
        store.update(deleteAllEntities(), (state) => ({
          ...state,
          loaded: false,
        }));
      }
    });
  }

  public setActive(generator: Generator) {
    store.update((state) => ({ ...state, activeGenerator: generator }));
  }

  public load(): void {
    this.errorService.clearErrors();
    if (store.value.loading === true) {
      // already loading, no need to load again
      return;
    }

    this.loadingId = v4();

    const progressStateCallback = (data) => {
      store.update(upsertEntities(data), (state) => ({
        ...state,
      }));
    };

    const calls: Observable<Generator[]>[] = [];
    if (this.accessList.includes('*')) {
      calls.push(
        this.service.findAll('*', this.loadingId, progressStateCallback)
      );
    } else if (this.accessList || this.accessList.length > 0) {
      store.update((state) => ({ ...state, loading: true, loaded: false }));
      for (let i = 0; i < this.accessList.length; i++) {
        calls.push(
          this.service.findAll(
            this.accessList[i],
            this.loadingId,
            progressStateCallback
          )
        );
      }
    }

    if (calls && calls.length > 0) {
      store.update((state) => ({ ...state, loading: true, loaded: false }));
    }
    forkJoin(calls).subscribe({
      next: (callResults) => {
        const generators = callResults.reduce((result, current) => [
          ...result,
          ...current,
        ]);
        store.update(upsertEntities(generators), (state) => ({
          ...state,
        }));
        store.update((state) => ({ ...state, loading: false, loaded: true }));
        this.loadingId = '';
      },
      error: (error) => {
        this.loadingId = '';
        this.errorService.raiseError(error);
        store.update((state) => ({ ...state, loading: false }));
      },
    });
  }

  public upsert(generator: Generator) {
    this.errorService.clearErrors();
    if (generator.id) {
      this.update(generator.id, generator);
    } else {
      this.add(generator);
    }
  }

  public add(newGenerator: Generator) {
    this.errorService.clearErrors();
    store.update((state) => ({ ...state, loading: true }));
    this.service
      .create({ ...newGenerator, customerName: this.selectedCustomer })
      .subscribe(
        (generator) => {
          store.update(upsertEntities(generator), (state) => ({
            ...state,
            activeGenerator: generator,
            loading: false,
          }));
        },
        (error) => {
          this.errorService.raiseError(error);
          store.update((state) => ({ ...state, loading: false }));
        },
        () => store.update((state) => ({ ...state, loading: false }))
      );
  }

  public update(id: string, generator: Generator) {
    this.errorService.clearErrors();
    store.update((state) => ({ ...state, loading: true }));
    this.service.update(id, generator).subscribe(
      (updatedGenerator) => {
        store.update(upsertEntities(updatedGenerator), (state) => ({
          ...state,
          activeGenerator: updatedGenerator,
          loading: false,
        }));
      },
      (error) => {
        this.errorService.raiseError(error);
        store.update((state) => ({ ...state, loading: false }));
      },
      () => store.update((state) => ({ ...state, loading: false }))
    );
  }

  public deleteGenerator(id: string) {
    this.errorService.clearErrors();
    store.update((state) => ({ ...state, loading: true }));
    this.service.deleteGenerator(id).subscribe(
      (updatedGenerator) => {
        this.setActive(null);
        this.updateCollection(updatedGenerator, { remove: true });
      },
      (error) => {
        this.errorService.raiseError(error);
        store.update((state) => ({ ...state, loading: false }));
      },
      () => store.update((state) => ({ ...state, loading: false }))
    );
  }

  // TODO: We can potentially read from the state colelction, and fetch only if necessary
  // There are some open design questions regarding maintaining this state.
  public findById(generatorId: string): Observable<Generator | never> {
    this.errorService.clearErrors();
    store.update((state) => ({ ...state, loading: true }));

    if (store.value.activeGenerator?.id !== generatorId) {
      // reset the active profile if we're loading a different one
      this.setActive(null);
    }

    if (!this.findRequests[generatorId]) {
      // cache the request for this waste profile
      this.findRequests[generatorId] = this.service.find(generatorId).pipe(
        tap((wasteProfile) => {
          this.setActive(wasteProfile);
          this.updateCollection(wasteProfile);
        }),
        catchError((error) => {
          this.errorService.raiseError(error);
          store.update((state) => ({ ...state, loading: false }));

          return throwError(error);
        }),
        finalize(() => {
          // remove the pending call from the cache upon completion
          const { [generatorId]: request, ...findRequests } = this.findRequests;
          this.findRequests = findRequests;
          store.update((state) => ({ ...state, loading: false }));
        })
      );
    }

    return this.findRequests[generatorId];
  }

  private updateCollection(
    updates: Generator | Generator[],
    options = { remove: false }
  ) {
    let updateArray: Generator[] = [];
    if (!Array.isArray(updates)) {
      updateArray = [updates];
    } else {
      updateArray = updates;
    }

    if (options.remove) {
      this.removeGeneratorFromCollection(updateArray);
    } else {
      store.update(upsertEntities(updateArray));
    }
  }

  private removeGeneratorFromCollection(generators: Generator[]) {
    const ids = generators.map((generator) => generator.id);
    store.update(deleteEntities(ids));
  }
}
